import React, { useState, useRef } from 'react';
import { Camera, X, CheckCircle, AlertTriangle, Loader2, FileImage } from '../common/Icons';
import { AIProcessingService } from '../../services/aiProcessingService';
import { StorageService } from '../../services/storageService';

/**
 * Enhanced ScreenshotUploader that handles both AI processing and file upload
 * Extends the basic ScreenshotUploader with evidence storage capabilities
 */
const EnhancedScreenshotUploader = ({
  onProcessingComplete,
  onProcessingError,
  onFileUpload,
  matchContext,
  className = '',
  disabled = false,
  showUploadedPreview = true
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (disabled || isProcessing || isUploading) return;

    try {
      // Validate file and show preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setUploadedFile(file);
      setProcessingStatus(null);
      
      // Start AI processing
      await processScreenshot(file);
      
    } catch (error) {
      console.error('File selection error:', error);
      setPreview(null);
      setUploadedFile(null);
      setProcessingStatus({
        type: 'error',
        message: error.message
      });
      onProcessingError?.(error);
    }
  };

  const processScreenshot = async (file) => {
    setIsProcessing(true);
    setProcessingProgress(0);
    
    try {
      // Progress indicators
      setProcessingStatus({
        type: 'processing',
        message: 'Compressing image...'
      });
      setProcessingProgress(20);

      // Process screenshot for AI
      await StorageService.processScreenshotForAI(file);
      setProcessingProgress(40);

      setProcessingStatus({
        type: 'processing',
        message: 'Analyzing screenshot with AI...'
      });

      // Send to AI processing
      const result = await AIProcessingService.processMatchScreenshot(file, matchContext);
      setProcessingProgress(80);

      setProcessingStatus({
        type: 'processing',
        message: 'Finalizing results...'
      });

      setProcessingProgress(100);

      // Success handling
      setProcessingStatus({
        type: 'success',
        message: `AI found ${result.players?.length || 0} players with ${Math.round(result.confidence * 100)}% confidence`
      });

      // Call the completion handler with both result and file
      onProcessingComplete?.(result, file);
      
    } catch (error) {
      console.error('AI processing error:', error);
      setProcessingStatus({
        type: 'error',
        message: error.message || 'AI processing failed'
      });
      onProcessingError?.(error);
    } finally {
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileSelect(imageFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !isProcessing && !isUploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const clearPreview = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setUploadedFile(null);
    setProcessingStatus(null);
    setProcessingProgress(0);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const isLoading = isProcessing || isUploading;
  const hasFile = preview && uploadedFile;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-4 transition-all duration-200
          ${dragOver ? 'border-[#31C590] bg-[#31C590]/10' : 'border-white/10'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#31C590]/50'}
          ${isLoading ? 'pointer-events-none' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={!disabled && !isLoading ? triggerFileInput : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          className="hidden"
        />

        {/* Content */}
        <div className="text-center">
          {isLoading ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Loader2 className="w-12 h-12 text-[#31C590] animate-spin" />
              </div>
              <div className="space-y-2">
                <div className="text-lg font-medium text-white">
                  {isProcessing ? 'Processing Screenshot...' : 'Uploading...'}
                </div>
                {processingStatus && (
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {processingStatus.message}
                  </div>
                )}
                {processingProgress > 0 && (
                  <div className="w-full bg-[#22262f] rounded-full h-2">
                    <div
                      className="bg-[#31C590] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${processingProgress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : hasFile ? (
            <div className="space-y-4">
              <div className="relative inline-block">
                <img
                  src={preview}
                  alt="Screenshot preview"
                  className="max-w-full max-h-32 rounded-lg border border-gray-200 dark:border-zinc-600"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearPreview();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 transition-colors"
                  title="Remove screenshot"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              
              {processingStatus && (
                <div className={`
                  flex items-center justify-center space-x-2 p-3 rounded-lg
                  ${processingStatus.type === 'success' ? 'bg-[#31C590]/10 text-[#23F8B5]' : ''}
                  ${processingStatus.type === 'error' ? 'bg-red-500/10 text-red-300' : ''}
                `}>
                  {processingStatus.type === 'success' && <CheckCircle className="w-5 h-5" />}
                  {processingStatus.type === 'error' && <AlertTriangle className="w-5 h-5" />}
                  <span className="text-sm font-medium">{processingStatus.message}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2.5">
              <div className="flex justify-center">
                <div className="p-2.5 bg-[#31C590]/15 rounded-full">
                  <Camera className="w-6 h-6 text-[#23F8B5]" />
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-base font-semibold text-white">
                  Upload Match Screenshot
                </div>
                <div className="text-sm text-gray-400">
                  Drop your screenshot here or click to browse
                </div>
                <div className="text-xs text-gray-500">
                  Supported formats: PNG, JPG, WebP (max 10MB)
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-[#22262f] border border-white/[0.07] rounded-lg p-3">
        <div className="flex items-start space-x-3">
          <FileImage className="w-5 h-5 text-[#23F8B5] mt-0.5 flex-shrink-0" />
          <div className="space-y-2 text-sm">
            <div className="font-medium text-white">
              Screenshot Tips for Better AI Recognition:
            </div>
            <ul className="text-gray-400 space-y-1 list-disc list-inside text-xs">
              <li>Capture the full scoreboard including player names and stats</li>
              <li>Ensure good image quality with clear, readable text</li>
              <li>Include team scores at the top of the screenshot</li>
              <li>Make sure player statistics are fully visible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedScreenshotUploader;